/**
 * **La cérémonie d'inauguration de la rue** ⟦A-71⟧ — le dernier moment du jeu.
 *
 * ## ⟦C61 · A-178⟧ Refaite dans la DA, plein écran
 *
 * Matt, 03/10/2026 : *« J'ai aussi vu la célébration de fin, c'est catastrophique. »* La version
 * d'avant était une boîte de dialogue sur la chambre assombrie : des bonshommes en fil de fer, un
 * cadre vide dessiné en SVG (des placeholders jamais remplacés), des paragraphes et des points
 * d'étape. Elle est maintenant une **séquence plein écran de quatre tableaux de pixel art**, dans
 * la DA « Système D, 8-bit » (dioramas isométriques sur fond nuit, la série `assets/stills-8bit/`) :
 *
 * 1. la foule arrive dans une rue de Castelclic (fanions, voisins, fans) ;
 * 2. le maire (écharpe), le ruban, la Mamie au premier rang ;
 * 3. la plaque dévoilée : la plaque bleue est VIERGE dans l'image, le nom de la chaîne est posé
 *    par-dessus en police pixel du jeu (`game/ceremonie.ts` calcule l'homographie qui le plaque
 *    sur le mur : jamais de texte généré dans l'image) ;
 * 4. la fête : confettis, la foule qui applaudit, l'avatar du joueur qui saute de joie.
 *
 * **L'avatar du joueur** (ses sprites existants, f ou h) est composé par-dessus : ce n'est pas un
 * personnage générique, c'est lui. Une ligne courte par tableau (douze mots au plus), qui avance
 * seule ou au clic ; « Passer » est discret, en haut à droite, et visible dès le premier tableau.
 *
 * Les tableaux ne se coupent jamais sèchement : fondu de l'un à l'autre, travelling lent sur
 * l'image, fondu de sortie vers l'écran du million. Les images ont été tirées en local (klein9b,
 * zéro crédit) : `assets/making-of/2026-10-03-C61-celebration/`.
 *
 * ## 🔴 Le son décore, il ne décide de rien
 *
 * `rue_fanfare` accompagne la séquence. Un joueur qui a coupé le son voit **exactement la même
 * cérémonie** : les tableaux s'enchaînent sur l'horloge du composant, jamais sur la position de la
 * lecture. La musique n'apporte qu'une chose, décorative : un **calage des durées** sur un nombre
 * entier de temps (`calerSurLesTemps`), qui déplace la fin d'un tableau de quelques centaines de
 * millisecondes au plus.
 *
 * ⚠️ Rien ici n'est noté, chronométré ni comparé. C'est ce qui rend ce calage acceptable : ⟦A-22⟧
 * interdit qu'une réussite dépende de l'oreille, et il n'y a pas de réussite dans une cérémonie. Le
 * jour où une mécanique *jouée* voudra se caler sur une grille, la règle change de camp — elle est
 * écrite en tête de `audio/beatmap.ts`.
 *
 * ## Ce qu'elle sait désormais ⟦A-74⟧
 *
 * `state.ending` est nommé par le moteur depuis M3.9 (`fins.ts`, `determinerFin`). Comme A-71
 * l'avait prévu, c'est **la ligne du dernier tableau qui s'adapte** — jamais la porte d'entrée : la
 * cérémonie reste célébrée pour tout le monde (la rue, le million la donne à tous, PLAN ligne 3).
 *
 * ⟦C57 · lot 7 · A-174⟧ La ligne de chaque tableau est de la narration (`data-narration`) : elle
 * reste à l'écran, la mesure de conformité la range à part.
 */

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";

import type { EndingId, GenreAvatar } from "@maxime/shared";

import { calerSurLesTemps } from "../../audio/beatmap";
import { useMusique, useSfx } from "../../components/SoundProvider";
import {
  TABLEAUX,
  confettis,
  homographie,
  lignesDuNom,
  ligneDuTableau,
  matrice3d,
  spriteCeremonie,
  taillePourTenir,
  zoneUtile,
  type QuadPlaque,
  type Tableau,
} from "../../game/ceremonie";
import { useUIStore } from "../../stores/uiStore";

/** Le repère de la plaque : un bloc de 400 × 160 « pixels de plaque », plaqué sur le mur. */
const PLAQUE_L = 400;
const PLAQUE_H = 160;
/** Le tableau est dessiné dans un repère de 1 000 unités, mis à l'échelle de l'écran (`--k`). */
const REPERE = 1000;
/** La marge du cadre blanc de la plaque par défaut (part de chaque côté). */
const MARGE_PLAQUE = { x: 0.09, y: 0.13 };
/** Durée du fondu de sortie vers l'écran du million. */
const SORTIE_MS = 520;

const CONFETTIS = confettis();

/** Le nom de la chaîne, posé sur la plaque vierge de l'image, en police pixel. */
function NomSurPlaque({ quad, marge, nom }: { quad: QuadPlaque; marge: { x: number; y: number }; nom: string }) {
  // Le quad est en % du tableau ; le repère de dessin fait 1 000 unités : × 10.
  const utile = zoneUtile(quad, marge);
  const enRepere: QuadPlaque = {
    hautGauche: { x: utile.hautGauche.x * 10, y: utile.hautGauche.y * 10 },
    hautDroit: { x: utile.hautDroit.x * 10, y: utile.hautDroit.y * 10 },
    basGauche: { x: utile.basGauche.x * 10, y: utile.basGauche.y * 10 },
    basDroit: { x: utile.basDroit.x * 10, y: utile.basDroit.y * 10 },
  };
  const lignes = lignesDuNom(nom);
  const taille = taillePourTenir(lignes, PLAQUE_L, lignes.length > 1 ? 66 : 84);
  return (
    <div
      className="cer-nom"
      style={{
        width: PLAQUE_L,
        height: PLAQUE_H,
        transform: matrice3d(homographie(enRepere, PLAQUE_L, PLAQUE_H)),
      }}
    >
      <span className="cer-nom-rue">RUE DE</span>
      <span className="cer-nom-nom" style={{ fontSize: taille, lineHeight: 0.92 }}>
        {lignes.map((l) => (
          <span key={l} className="cer-nom-ligne">
            {l}
          </span>
        ))}
      </span>
    </div>
  );
}

/** Un tableau : l'image, le nom sur la plaque, les confettis, l'avatar — tout dans le même repère. */
function Vue({
  tableau,
  nomChaine,
  genre,
  actif,
  passe,
  mouvementReduit,
}: {
  tableau: Tableau;
  nomChaine: string;
  genre: GenreAvatar | null;
  actif: boolean;
  passe: boolean;
  mouvementReduit: boolean;
}) {
  const { place, camera } = tableau;
  const style = {
    ["--duree" as string]: `${tableau.dureeMs + 900}ms`,
    ["--zoom-de" as string]: camera.de,
    ["--zoom-vers" as string]: camera.vers,
    ["--cam-x" as string]: `${camera.x}%`,
    ["--cam-y" as string]: `${camera.y}%`,
  } as CSSProperties;
  const classes = ["cer-vue", actif ? "actif" : "", passe ? "passe" : "", mouvementReduit ? "pose" : ""]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={classes} style={style} data-tableau={tableau.id} aria-hidden="true">
      <div className="cer-camera">
        <img className="cer-image" src={tableau.image} alt="" draggable={false} />
        {tableau.plaque ? (
          <div className="cer-repere">
            <NomSurPlaque quad={tableau.plaque} marge={tableau.margePlaque ?? MARGE_PLAQUE} nom={nomChaine} />
          </div>
        ) : null}
        {tableau.confettis ? (
          <div className="cer-confettis">
            {CONFETTIS.map((c, i) => (
              <i
                key={i}
                className={`cer-confetti c${c.couleur}`}
                style={{
                  left: `${c.x}%`,
                  width: `${c.taille}%`,
                  height: `${c.taille}%`,
                  animationDelay: `${c.delai}s`,
                  animationDuration: `${c.duree}s`,
                  ["--derive" as string]: c.derive,
                  ["--haut" as string]: Math.round(c.delai * 25),
                }}
              />
            ))}
          </div>
        ) : null}
        <div
          className={`cer-heros${place.saute ? " saute" : ""}`}
          style={{ left: `${place.x}%`, top: `${place.y}%`, height: `${place.hauteur}%` }}
        >
          <img className="cer-sprite" src={spriteCeremonie(genre, place)} alt="" draggable={false} />
        </div>
      </div>
    </div>
  );
}

export function Ceremonie({
  nomChaine,
  fin,
  genre,
  onFini,
}: {
  nomChaine: string;
  /** ⟦A-74⟧ La fin nommée par le moteur (M3.9) — `null` sur un état d'avant. */
  fin: EndingId | null;
  /** Le genre de l'avatar du joueur — `null` sur un état d'avant ⟦A-4 §2⟧ (sprite R3 du jeu). */
  genre: GenreAvatar | null;
  onFini: () => void;
}) {
  const musique = useMusique();
  const sfx = useSfx();
  const reducedMotion = useUIStore((s) => s.reducedMotion);
  const [index, setIndex] = useState(0);
  const [sortie, setSortie] = useState(false);
  const [cote, setCote] = useState(0);
  const racine = useRef<HTMLDivElement>(null);
  const scene = useRef<HTMLDivElement>(null);

  // `onFini` change d'identité à chaque rendu du parent : la garder dans une ref évite de
  // relancer la minuterie du tableau en cours à cause d'une prop qui n'a pas changé de sens.
  const fini = useRef(onFini);
  fini.current = onFini;

  // Le côté du tableau en pixels : le nom sur la plaque est dessiné dans un repère fixe de 1 000
  // unités, mis à l'échelle ici (`--k`). Mesuré, jamais deviné : il dépend de l'écran.
  useLayoutEffect(() => {
    const el = scene.current;
    if (!el) return;
    const mesurer = () => setCote(el.clientWidth);
    mesurer();
    const obs = new ResizeObserver(mesurer);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Le clavier : la cérémonie reçoit le focus, Entrée / Espace / → avancent, Échap la passe.
  useEffect(() => {
    racine.current?.focus({ preventScroll: true });
  }, []);

  // La musique démarre au montage. Elle ne bloque rien : `jouer` ne rend rien et n'attend rien.
  useEffect(() => {
    musique?.jouer("rue_fanfare", { volume: 0.7, fondueMs: 900 });
    return () => musique?.arreter({ fondueMs: 700 });
  }, [musique]);

  // ── L'enchaînement des tableaux ───────────────────────────────────────────────────────
  //
  // 🔑 La durée se calcule **au démarrage de chaque tableau**, pas une fois pour toutes au
  // montage : la grille de la musique arrive de manière asynchrone, et un calcul fait trop tôt la
  // manquerait toujours. Le clic avance aussi : changer `index` relance cette minuterie.
  useEffect(() => {
    if (index >= TABLEAUX.length) return;
    const duree = calerSurLesTemps(musique?.grilleEnCours() ?? null, TABLEAUX[index]!.dureeMs);
    const minuterie = window.setTimeout(() => setIndex((i) => i + 1), duree);
    return () => window.clearTimeout(minuterie);
  }, [index, musique]);

  // Les sons de la séquence : décoratifs, et jamais sur la position de la musique. Le glissement
  // à chaque changement de tableau, le jingle de la plaque quand le nom apparaît, celui des paliers
  // et les applaudissements quand les confettis tombent.
  useEffect(() => {
    if (index >= TABLEAUX.length) return;
    if (index > 0 && !reducedMotion) sfx("fenetre_glisse", { volume: 0.45 });
    const id = TABLEAUX[index]!.id;
    if (id === "plaque") {
      const t = window.setTimeout(() => sfx("plaque_jingle"), 700);
      return () => window.clearTimeout(t);
    }
    if (id === "fete") {
      const t = window.setTimeout(() => {
        sfx("palier_jingle");
        sfx("ceremonie_applaudissements", { volume: 0.7 });
      }, 300);
      return () => window.clearTimeout(t);
    }
  }, [index, sfx, reducedMotion]);

  // La sortie : fondu vers l'écran du million, puis on rend la main — jamais de coupe sèche.
  useEffect(() => {
    if (index < TABLEAUX.length) return;
    setSortie(true);
    const t = window.setTimeout(() => fini.current(), reducedMotion ? 0 : SORTIE_MS);
    return () => window.clearTimeout(t);
  }, [index, reducedMotion]);

  const suivant = () => setIndex((i) => Math.min(i + 1, TABLEAUX.length));
  const passer = () => setIndex(TABLEAUX.length);

  const actuel = TABLEAUX[Math.min(index, TABLEAUX.length - 1)]!;

  return (
    <div
      ref={racine}
      className={`cer${sortie ? " sortie" : ""}${reducedMotion ? " pose" : ""}`}
      role="region"
      aria-label={`Rue ${nomChaine || "sans nom"} : inauguration`}
      tabIndex={-1}
      data-tableau={actuel.id}
      style={{ ["--k" as string]: cote / REPERE } as CSSProperties}
      onClick={suivant}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          passer();
        } else if (e.key === "Enter" || e.key === " " || e.key === "ArrowRight") {
          e.preventDefault();
          suivant();
        }
      }}
    >
      <div className="cer-scene" ref={scene}>
        {TABLEAUX.map((t, i) => (
          <Vue
            key={t.id}
            tableau={t}
            nomChaine={nomChaine}
            genre={genre}
            actif={i === index}
            passe={i < index}
            mouvementReduit={reducedMotion}
          />
        ))}
      </div>

      <div className="cer-bas">
        <div className="fenetre cer-boite">
          <div className="fenetre-inner">
            {/* La `key` force le remplacement du bloc à chaque tableau : sans elle, React réutilise
                le nœud et l'apparition en fondu ne se rejoue pas. */}
            <p key={actuel.id} className="cer-ligne" data-narration="fin" aria-live="polite">
              {ligneDuTableau(actuel.id, { fin })}
            </p>
            <span className="cer-suite" aria-hidden="true" />
          </div>
        </div>
      </div>

      <button
        className="bouton discret cer-passer"
        type="button"
        aria-label="Passer la cérémonie"
        onClick={(e) => {
          e.stopPropagation();
          passer();
        }}
      >
        Passer
      </button>
    </div>
  );
}
